import {Injectable} from '@angular/core'
import {UveLogKey} from '@models/constants/log-key'
import {Endpoints} from '@models/endpoints'
import {OfferNM, OrderGiftOffersData, ProductOfferNM} from '@models/freeProducts'
import {OfferGiftOnlineService} from './offer-gift-online.service'

/**
 * Variante embebida de `OfferGiftOnlineService`.
 *
 * @description
 * Además de los productos que devuelve `PRODUCT_FREE`, llama a `PRODUCTS_INFO` para
 * completar metadatos (imagen, idcProducto) de los productos ofertados, y paginación
 * en lotes de 100 cuando la oferta tiene muchos candidatos. Cachea las imágenes en
 * `ProductService.addProductImageByIdcProducto` para evitar descargas repetidas.
 *
 * Se usa en el flujo embebido en UVE Cloud, donde el catálogo local no está completo.
 */
@Injectable({
  providedIn: 'root',
})
export class OfferGiftOnlineEmbeddedService extends OfferGiftOnlineService {
  getProductInfo = async (offer: OfferNM, data: any): Promise<ProductOfferNM> => {
    const params = {
      additionalInfo: JSON.stringify({
        customerId: data?.customerCode?.trim() ? data.customerCode : data.customerId,
      }),
      isInternalCode: !!data?.customerCode?.trim(),
      model: data.orderModel.Id,
      orderType: data.orderModel.Type,
      withPrices: data.orderModel.Valued,
      q: offer.hierarchyId,
    }

    const productList = await this.getProductsCall(params)

    const productData: any = await this.productService.uveApiService.getPromise(Endpoints.PRODUCTS_INFO, null, [
      {id: '{productCodes}', value: offer.hierarchyId},
    ])

    let productInfo = null
    if (!!productData.data && productData.data[0]) {
      productInfo = productData.data?.find(p => p.code === offer.hierarchyId) ?? productData.data[0]
      if (productInfo.picture?.url) {
        this.productService.addProductImageByIdcProducto(productInfo.idcProducto, {
          url: productInfo.picture?.url,
        })
      }
    }
    const product = {
      idcProducto: productInfo?.idcProducto,
      id: productInfo?.id,
      code: offer.hierarchyId,
      name: productList[0]?.name,
      selectedAmount: offer.amount,
      image: productList[0]?.additionalInfo?.image,
      additionalInfo: productList[0]?.additionalInfo,
    }

    return product
  }

  override prepareOfferProducts = async (offer: OfferNM, dataInfo: OrderGiftOffersData): Promise<ProductOfferNM[]> => {
    try {
      if (!dataInfo.customerCode) {
        throw new Error('customerCode is not defined in dataInfo object.')
      }
      const params = {
        additionalInfo: `{"customerId": "${dataInfo.customerCode}","${offer.hierarchyLevelId}": "${offer.hierarchyId}"}`,
        isInternalCode: true,
        model: dataInfo.orderModel.Id,
      }

      const productFreeItems = await this.getProductsCall(params)
      const limit = 100
      let productData: any = {data: []}
      if (productFreeItems?.length > limit) {
        const batches = productFreeItems.length / limit + 1
        const promises = []
        let batch = 0
        while (batch < batches) {
          const batchItems = productFreeItems.slice(batch * limit, (batch + 1) * limit)
          promises.push(
            this.productService.uveApiService.getPromise(Endpoints.PRODUCTS_INFO, null, [
              {id: '{productCodes}', value: batchItems.map(p => p.id).join(',')},
            ])
          )
          batch++
        }
        productData.data = await Promise.all(promises).then(responses =>
          responses.reduce((pd, r) => pd.concat(r.data), [])
        )
      } else {
        productData = await this.productService.uveApiService.getPromise(Endpoints.PRODUCTS_INFO, null, [
          {id: '{productCodes}', value: productFreeItems.map(p => p.id).join(',')},
        ])
      }

      productData.data?.forEach(p => {
        if (p.picture?.url) {
          this.productService.addProductImageByIdcProducto(p.idcProducto, {
            url: p.picture?.url,
          })
        }
      })

      return productFreeItems.map(p => {
        const productInfo = productData.data.find(pd => pd.code === p.id)
        return {
          idcProducto: productInfo?.idcProducto,
          id: productInfo?.id,
          code: p.id,
          name: p.name,
          selectedAmount: 0,
          image: p?.additionalInfo?.image,
          additionalInfo: p?.additionalInfo,
        }
      })
    } catch (error) {
      this.toastNotificationService.error(this.translationServicex.instant('alert_message_offers_error'))
      this.loggingService.error(UveLogKey.Error.OnPrepareOfferProducts, {error})
    }
  }
}
